import { useStoreId } from "@coworker/app/src/core/auth/useLoggedInUser";
import { httpClient } from "@coworker/http-client";
import { useQuery } from "@tanstack/react-query";

type InsightsDataFilters = {
  itemNo?: string;
  refillAreaId?: string;
  itemRefillLocationGridCode?: string;
  itemHfbNo?: string;
  selectedPeriod: string;
};

export type InsightsTablesType =
  | "articleInsights"
  | "refilledAreaInsights"
  | "refilledSalesLocationIDInsights"
  | "itemHfbTypeInsights";

export type InsightsOrderByColumn =
  | "estimatedTurnover"
  | "pieces"
  | "numberOfRefillLocations"
  | "numberOfArticles";

export type InsightsOrderByValues = {
  column: InsightsOrderByColumn;
  direction: "asc" | "desc";
};

export type InsightsOrderByColumns = {
  [table in InsightsTablesType]: InsightsOrderByValues;
};

type InsightsTablePages = {
  [table in InsightsTablesType]: number;
};

type InsightsEndpoints =
  | "articles"
  | "refill_areas"
  | "refilled_sales_location_ids"
  | "item_hfb_types"
  | "total_counts";

const getInsightsEndpointUrl = (
  storeId: string,
  endpoint: InsightsEndpoints,
  filters: InsightsDataFilters,
  pageParam: number,
  orderByValue?: string
) => {
  const pageSize = 5;

  let url =
    endpoint === "total_counts"
      ? `insights/total_counts?storeId=${encodeURIComponent(storeId)}&selectedPeriod=${encodeURIComponent(filters.selectedPeriod)}&`
      : `insights/${endpoint}?storeId=${encodeURIComponent(storeId)}&selectedPeriod=${encodeURIComponent(filters.selectedPeriod)}${orderByValue}&pageSize=${pageSize}&page=${pageParam}&`;

  for (const key in filters) {
    if (key === "selectedPeriod") continue; // already added above — required field must never be skipped
    const value = filters[key as keyof InsightsDataFilters];

    if (!value) {
      continue;
    }

    url += `${key}=${encodeURIComponent(value)}&`;
  }

  return url;
};

const fetchInsights = async (
  storeId: string,
  endpoint: InsightsEndpoints,
  filters: InsightsDataFilters,
  pageParam: number,
  orderByColumn?: string
) => {
  const path = `/${getInsightsEndpointUrl(storeId, endpoint, filters, pageParam, orderByColumn)}`;

  const data = await httpClient.tasks.get<unknown>(path);

  if (endpoint === "total_counts" && !!data) {
    return data;
  }

  if (data) {
    return { ...(data as object), pageParam };
  }

  return { rows: [], totalRowCount: 0, pageParam };
};

export const useInsightsData = (
  tablePages: InsightsTablePages,
  filters: InsightsDataFilters,
  orderByColumn: InsightsOrderByColumns
) => {
  const storeId = useStoreId();
  const filtersIndex = [];
  for (const key in filters) {
    if (filters[key as keyof InsightsDataFilters]) {
      filtersIndex.push(`${key}=${filters[key as keyof InsightsDataFilters]}`);
    }
  }
  filtersIndex.sort();

  const orderByValue = (orderBy: {
    column: InsightsOrderByColumn;
    direction: "asc" | "desc";
  }) => {
    if (!orderBy) return "";
    return `&orderByColumn=${orderBy.column}&orderDirection=${orderBy.direction}`;
  };

  const articleInsightsQueryResult = useQuery({
    queryKey: [
      "insights",
      "articleInsights",
      tablePages.articleInsights,
      ...filtersIndex,
      orderByValue(orderByColumn.articleInsights),
    ],
    queryFn: async () => {
      return await fetchInsights(
        storeId,
        "articles",
        filters,
        tablePages.articleInsights,
        orderByValue(orderByColumn.articleInsights)
      );
    },
    enabled: !!storeId && !!filters.selectedPeriod,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const refilledAreaInsightsQueryResult = useQuery({
    queryKey: [
      "insights",
      "refilledAreaInsights",
      tablePages.refilledAreaInsights,
      ...filtersIndex,
      orderByValue(orderByColumn.refilledAreaInsights),
    ],
    queryFn: async () => {
      return await fetchInsights(
        storeId,
        "refill_areas",
        filters,
        tablePages.refilledAreaInsights,
        orderByValue(orderByColumn.refilledAreaInsights)
      );
    },
    enabled: !!storeId && !!filters.selectedPeriod,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const refilledSalesLocationIDInsightsQueryResult = useQuery({
    queryKey: [
      "insights",
      "refilledSalesLocationIDInsights",
      tablePages.refilledSalesLocationIDInsights,
      ...filtersIndex,
      orderByValue(orderByColumn.refilledSalesLocationIDInsights),
    ],
    queryFn: async () => {
      return await fetchInsights(
        storeId,
        "refilled_sales_location_ids",
        filters,
        tablePages.refilledSalesLocationIDInsights,
        orderByValue(orderByColumn.refilledSalesLocationIDInsights)
      );
    },
    enabled: !!storeId && !!filters.selectedPeriod,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const itemHfbTypeInsightsQueryResult = useQuery({
    queryKey: [
      "insights",
      "itemHfbTypeInsights",
      tablePages.itemHfbTypeInsights,
      ...filtersIndex,
      orderByValue(orderByColumn.itemHfbTypeInsights),
    ],
    queryFn: async () => {
      return await fetchInsights(
        storeId,
        "item_hfb_types",
        filters,
        tablePages.itemHfbTypeInsights,
        orderByValue(orderByColumn.itemHfbTypeInsights)
      );
    },
    enabled: !!storeId && !!filters.selectedPeriod,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const totalCounts = useQuery<{ pieces?: string; estimatedTurnover?: string }>(
    {
      queryKey: ["insights", "totalCounts", ...filtersIndex],
      queryFn: async () =>
        (await fetchInsights(storeId, "total_counts", filters, 1)) as {
          pieces?: string;
          estimatedTurnover?: string;
        },
      enabled: !!storeId && !!filters.selectedPeriod,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }
  );

  return {
    articleInsights: articleInsightsQueryResult,
    refilledAreaInsights: refilledAreaInsightsQueryResult,
    refilledSalesLocationIDInsights: refilledSalesLocationIDInsightsQueryResult,
    itemHfbTypeInsights: itemHfbTypeInsightsQueryResult,
    totalCounts: totalCounts,
  };
};
